/**
 * LegalTemplates PDF Editor — utilities and helpers shared across the editor
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { ensurePdfLib, libs } from './context.js';
import {
    getTextAnnotationContentRect,
    hasPendingAnnotationChanges,
    onPagePointerDown,
    onPagePointerLeave,
    onPagePointerMove,
    onPagePointerUp,
    onPageTouchStart,
} from './annotations.js';
import { documentRequiredTopActions, pagesEl, publicEditorMode, session, state } from './context.js';
import { hasPendingFormFieldChanges } from './form-fill.js';
import { hasPendingProtectionChanges, hasPendingWatermarkChanges } from './modals.js';
import { finalizeBlockTextLine, getFragmentBaseline, getTextAnnotationBounds } from './text.js';
import { idleBreath, pickNextPageToPrepare, preparePage } from './viewport.js';

/**
 * Guest editor: tag a working-file request with this tab's document id so
 * the server touches the right file. Member URLs already name the document.
 */
export function publicDocUrl(url) {
    if (!url || !publicEditorMode || !session.docId) {
        return url;
    }
    const text = String(url);
    if (/[?&]doc=/.test(text)) {
        return text;
    }

    return text + (text.includes('?') ? '&' : '?') + 'doc=' + encodeURIComponent(session.docId);
}

/**
 * Keep the tab's address pinned to its document, so a reload (or a
 * bookmark) reopens THIS document rather than whichever one was saved last.
 */
export function pinDocumentUrl() {
    if (!publicEditorMode || !session.docId || typeof window === 'undefined' || !window.history) {
        return;
    }
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('doc') === session.docId) {
            return;
        }
        url.searchParams.set('doc', session.docId);
        url.searchParams.delete('v');
        window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
        // Sandboxed or file: contexts: the id still rides every request.
    }
}

export function toUserFriendlyEditorErrorMessage(message) {
    const rawMessage = String(message || '').trim();
    if (rawMessage === '') {
        return rawMessage;
    }

    const normalized = rawMessage.toLowerCase();
    const isEncryptionError = (
        (normalized.includes('pdfdocument.load') && normalized.includes('encrypt')) ||
        normalized.includes('input document to `pdfdocument.load` is encrypted') ||
        normalized.includes('input document to pdfdocument.load is encrypted') ||
        normalized.includes('passwordexception') ||
        normalized.includes('no password given') ||
        normalized.includes('password required')
    );

    if (isEncryptionError) {
        return 'This PDF is password-protected. Remove the password and upload it again to edit, print, send, save, or download.';
    }

    return rawMessage;
}

export function hasPendingEdits() {
    return !!(
        hasPendingAnnotationChanges() ||
        state.activeTextEditor ||
        state.activeCommentEditor ||
        state.activeReplaceEditor ||
        state.activeBlockEditor ||
        state.activeFormFieldEditor ||
        state.draft ||
        hasPendingFormFieldChanges() ||
        hasPendingWatermarkChanges() ||
        hasPendingProtectionChanges()
    );
}

export function getBaseFileName() {
    const raw = String(session.docName || 'document')
        .replace(/\.(pdf|docx|xlsx|pptx|png|jpe?g)$/i, '')
        .trim();

    return raw || 'document';
}

export function formatFileSize(bytes) {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) {
        return '0.00 MB';
    }

    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(2)} KB`;
    }

    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

export function hasLoadedDocumentForActions() {
    return !!(state.pdfJsDoc && Number(state.pdfJsDoc.numPages || 0) > 0);
}

export function setButtonState(button, busy) {
    if (!button) {
        return;
    }

    button.dataset.busy = busy ? '1' : '0';
    const requiresDocument = documentRequiredTopActions.includes(button);
    const hasDocument = hasLoadedDocumentForActions();
    const shouldDisable = busy || (requiresDocument && !hasDocument);
    button.classList.toggle('disabled', shouldDisable);
    button.disabled = shouldDisable;
}

export function generateId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'ann-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

export function finiteNumber(value, fallback = null) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

export function hexToRgb(hex, fallback = '#111827') {
    const normalized = /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
    const value = normalized.replace('#', '');
    const r = parseInt(value.slice(0, 2), 16) / 255;
    const g = parseInt(value.slice(2, 4), 16) / 255;
    const b = parseInt(value.slice(4, 6), 16) / 255;
    return libs.PDFLib.rgb(r, g, b);
}

export function rgbToHex(r, g, b) {
    const toHex = (value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function parseHexColor(hex) {
    const normalized = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
        return null;
    }

    return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16),
    };
}

export function colorsAreClose(first, second, threshold = 42) {
    const firstRgb = parseHexColor(first);
    const secondRgb = parseHexColor(second);
    if (!firstRgb || !secondRgb) {
        return true;
    }

    const distance = Math.hypot(firstRgb.r - secondRgb.r, firstRgb.g - secondRgb.g, firstRgb.b - secondRgb.b);
    return distance <= threshold;
}

export function normalizeInkColor(hex, fallback = '#111827') {
    const rgb = parseHexColor(hex);
    if (!rgb) {
        return fallback;
    }

    const brightness = (rgb.r * 0.299) + (rgb.g * 0.587) + (rgb.b * 0.114);
    const chroma = Math.max(rgb.r, rgb.g, rgb.b) - Math.min(rgb.r, rgb.g, rgb.b);

    if (brightness < 170 && chroma < 28) {
        return '#000000';
    }

    return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function normalizeBackgroundColor(hex, fallback = '#ffffff') {
    const rgb = parseHexColor(hex);
    if (!rgb) {
        return fallback;
    }

    return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function sampleCanvasInkColor(context, canvas, fragment, dpr = 1) {
    if (!context || !canvas || !fragment) {
        return '';
    }

    const left = clamp(Math.floor(Number(fragment.x || 0) * dpr), 0, Math.max(0, canvas.width - 1));
    const top = clamp(Math.floor(Number(fragment.y || 0) * dpr), 0, Math.max(0, canvas.height - 1));
    const width = clamp(Math.ceil(Number(fragment.width || 1) * dpr), 1, Math.max(1, canvas.width - left));
    const height = clamp(Math.ceil(Number(fragment.height || 1) * dpr), 1, Math.max(1, canvas.height - top));
    const stepX = Math.max(1, Math.floor(width / 42));
    const stepY = Math.max(1, Math.floor(height / 16));
    let imageData;

    try {
        imageData = context.getImageData(left, top, width, height);
    } catch (error) {
        return '';
    }

    let weightedR = 0;
    let weightedG = 0;
    let weightedB = 0;
    let totalWeight = 0;
    const data = imageData.data;

    for (let y = 0; y < height; y += stepY) {
        for (let x = 0; x < width; x += stepX) {
            const index = ((y * width) + x) * 4;
            const alpha = data[index + 3];
            if (alpha < 32) {
                continue;
            }

            const r = data[index];
            const g = data[index + 1];
            const b = data[index + 2];
            const brightness = (r * 0.299) + (g * 0.587) + (b * 0.114);
            if (brightness > 244) {
                continue;
            }

            const weight = Math.max(1, 255 - brightness) * (alpha / 255);
            weightedR += r * weight;
            weightedG += g * weight;
            weightedB += b * weight;
            totalWeight += weight;
        }
    }

    if (totalWeight <= 0) {
        return '';
    }

    return normalizeInkColor(rgbToHex(weightedR / totalWeight, weightedG / totalWeight, weightedB / totalWeight));
}

export function sampleCanvasBackgroundColor(context, canvas, fragment, dpr = 1) {
    if (!context || !canvas || !fragment) {
        return '#ffffff';
    }

    const pad = Math.max(2, Math.ceil(Number(fragment.fontSize || 12) * 0.12));
    const left = clamp(Math.floor((Number(fragment.x || 0) - pad) * dpr), 0, Math.max(0, canvas.width - 1));
    const top = clamp(Math.floor((Number(fragment.y || 0) - pad) * dpr), 0, Math.max(0, canvas.height - 1));
    const width = clamp(Math.ceil((Number(fragment.width || 1) + (pad * 2)) * dpr), 1, Math.max(1, canvas.width - left));
    const height = clamp(Math.ceil((Number(fragment.height || 1) + (pad * 2)) * dpr), 1, Math.max(1, canvas.height - top));
    const stepX = Math.max(1, Math.floor(width / 52));
    const stepY = Math.max(1, Math.floor(height / 18));
    const buckets = new Map();
    let imageData;

    try {
        imageData = context.getImageData(left, top, width, height);
    } catch (error) {
        return '#ffffff';
    }

    const data = imageData.data;

    for (let y = 0; y < height; y += stepY) {
        for (let x = 0; x < width; x += stepX) {
            const index = ((y * width) + x) * 4;
            const alpha = data[index + 3];
            if (alpha < 32) {
                continue;
            }

            const r = data[index];
            const g = data[index + 1];
            const b = data[index + 2];
            const brightness = (r * 0.299) + (g * 0.587) + (b * 0.114);

            if (brightness < 150) {
                continue;
            }

            const bucketR = Math.round(r / 8) * 8;
            const bucketG = Math.round(g / 8) * 8;
            const bucketB = Math.round(b / 8) * 8;
            const key = `${bucketR},${bucketG},${bucketB}`;
            const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 };
            bucket.count += 1;
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
            buckets.set(key, bucket);
        }
    }

    let best = null;
    for (const bucket of buckets.values()) {
        if (!best || bucket.count > best.count) {
            best = bucket;
        }
    }

    if (!best || best.count <= 0) {
        return '#ffffff';
    }

    return normalizeBackgroundColor(rgbToHex(best.r / best.count, best.g / best.count, best.b / best.count));
}

export function getPageCanvasElement(pageNumber) {
    const pageShell = pagesEl.querySelector(`.pdf-page-shell[data-page="${pageNumber}"]`);
    if (!pageShell) {
        return null;
    }

    return pageShell.querySelector('.pdf-page-canvas');
}

export function isPixelLikelyLine(pixelData, pixelIndex) {
    const alpha = Number(pixelData[pixelIndex + 3] || 0);
    if (alpha < 170) {
        return false;
    }

    const red = Number(pixelData[pixelIndex] || 255);
    const green = Number(pixelData[pixelIndex + 1] || 255);
    const blue = Number(pixelData[pixelIndex + 2] || 255);
    const luminance = (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
    return luminance < 176;
}

export function getRectIntersectionArea(rectA, rectB) {
    if (!rectA || !rectB) {
        return 0;
    }

    const left = Math.max(Number(rectA.x || 0), Number(rectB.x || 0));
    const top = Math.max(Number(rectA.y || 0), Number(rectB.y || 0));
    const right = Math.min(
        Number(rectA.x || 0) + Math.max(0, Number(rectA.width || 0)),
        Number(rectB.x || 0) + Math.max(0, Number(rectB.width || 0))
    );
    const bottom = Math.min(
        Number(rectA.y || 0) + Math.max(0, Number(rectA.height || 0)),
        Number(rectB.y || 0) + Math.max(0, Number(rectB.height || 0))
    );

    const width = right - left;
    const height = bottom - top;
    if (width <= 0 || height <= 0) {
        return 0;
    }

    return width * height;
}

export function isRectCenterInside(rect, x, y) {
    if (!rect) {
        return false;
    }

    const rectX = Number(rect.x || 0);
    const rectY = Number(rect.y || 0);
    const rectW = Math.max(0, Number(rect.width || 0));
    const rectH = Math.max(0, Number(rect.height || 0));

    return x >= rectX && x <= (rectX + rectW) && y >= rectY && y <= (rectY + rectH);
}

export function isRectMeaningfullyInsideField(fieldRect, candidateRect) {
    const overlapArea = getRectIntersectionArea(fieldRect, candidateRect);
    if (overlapArea <= 0) {
        return false;
    }

    const candidateWidth = Math.max(1, Number(candidateRect.width || 0));
    const candidateHeight = Math.max(1, Number(candidateRect.height || 0));
    const candidateCenterX = Number(candidateRect.x || 0) + (candidateWidth / 2);
    const candidateCenterY = Number(candidateRect.y || 0) + (candidateHeight / 2);

    if (isRectCenterInside(fieldRect, candidateCenterX, candidateCenterY)) {
        return true;
    }

    const candidateArea = candidateWidth * candidateHeight;
    const fieldArea = Math.max(1, Number(fieldRect.width || 0) * Number(fieldRect.height || 0));
    const overlapByCandidate = overlapArea / candidateArea;
    const overlapByField = overlapArea / fieldArea;

    return overlapByCandidate >= 0.42 || overlapByField >= 0.16;
}

export function fieldHasVisibleTextContent(pageNumber, field, pageTextAnnotations = [], pageTextFragments = []) {
    if (!field || field.kind !== 'text') {
        return false;
    }

    const fieldRect = {
        x: Number(field.x || 0),
        y: Number(field.y || 0),
        width: Math.max(1, Number(field.width || 0)),
        height: Math.max(1, Number(field.height || 0)),
    };

    for (const annotation of pageTextAnnotations) {
        if (!annotation || annotation.type !== 'text' || annotation.page !== pageNumber) {
            continue;
        }

        if (!String(annotation.text || '').trim()) {
            continue;
        }

        const annotationRect = getTextAnnotationContentRect(annotation) || getTextAnnotationBounds(annotation);
        if (isRectMeaningfullyInsideField(fieldRect, annotationRect)) {
            return true;
        }
    }

    for (const fragment of pageTextFragments) {
        if (!fragment || !String(fragment.text || '').trim()) {
            continue;
        }

        const fragmentRect = {
            x: Number(fragment.x || 0),
            y: Number(fragment.y || 0),
            width: Math.max(1, Number(fragment.width || 0)),
            height: Math.max(1, Number(fragment.height || 0)),
        };

        if (fragmentRect.height > (fieldRect.height * 2.1)) {
            continue;
        }

        if (isRectMeaningfullyInsideField(fieldRect, fragmentRect)) {
            return true;
        }
    }

    return false;
}

export function distancePointToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
        return Math.hypot(px - x1, py - y1);
    }

    const t = clamp(((px - x1) * dx + (py - y1) * dy) / ((dx * dx) + (dy * dy)), 0, 1);
    const cx = x1 + (t * dx);
    const cy = y1 + (t * dy);
    return Math.hypot(px - cx, py - cy);
}

export function round1(value) {
    return Math.round(Number(value || 0) * 10) / 10;
}

// Rows of fragments sharing a baseline, split into segments at column-sized
// horizontal gaps (so pleading-paper line numbers, table cells, and the body
// column become separate lines even when they share a baseline).
export function buildPageTextLines(pageNumber) {
    const fragments = (state.pageTextFragments.get(pageNumber) || [])
        .filter((fragment) => fragment && String(fragment.text || '').trim() !== '')
        .slice()
        .sort((a, b) => (getFragmentBaseline(a) - getFragmentBaseline(b)) || (Number(a.x || 0) - Number(b.x || 0)));

    const rows = [];
    fragments.forEach((fragment) => {
        const baseline = getFragmentBaseline(fragment);
        const fontSize = Math.max(8, Number(fragment.fontSize || 12));
        const em = Number(fragment.glyphHeight) || fontSize;
        const row = rows.length ? rows[rows.length - 1] : null;
        const tolerance = Math.max(1.5, Math.max(em, row ? row.em : 0) * 0.4);
        if (row && Math.abs(baseline - row.baselineY) <= tolerance) {
            row.fragments.push(fragment);
            row.fontSize = Math.max(row.fontSize, fontSize);
            row.em = Math.max(row.em, em);
        } else {
            rows.push({ baselineY: baseline, fontSize, em, fragments: [fragment] });
        }
    });

    const lines = [];
    rows.forEach((row) => {
        const sorted = row.fragments.sort((a, b) => Number(a.x || 0) - Number(b.x || 0));
        let segment = [sorted[0]];
        for (let index = 1; index < sorted.length; index += 1) {
            const previous = sorted[index - 1];
            const current = sorted[index];
            const gap = Number(current.x || 0) - (Number(previous.x || 0) + Number(previous.width || 0));
            // ~1.9em of the TRUE glyph height (render units): wide enough that
            // word spacing never splits, tight enough to separate a
            // pleading-paper gutter or a form's column. A fixed 18px floor
            // merged every column of a tax form on a phone-sized render.
            const em = Math.max(
                Number(previous.glyphHeight) || Number(previous.fontSize || 12),
                Number(current.glyphHeight) || Number(current.fontSize || 12)
            );
            const columnGap = Math.max(4, em * 1.9);
            if (gap > columnGap) {
                lines.push(finalizeBlockTextLine(segment));
                segment = [current];
            } else {
                segment.push(current);
            }
        }
        lines.push(finalizeBlockTextLine(segment));
    });

    return lines.sort((a, b) => (a.baselineY - b.baselineY) || (a.x - b.x));
}

export function cssColorToHex(cssColor, fallback = '#111827') {
    const match = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(String(cssColor || ''));
    if (!match) {
        return /^#[0-9A-Fa-f]{6}$/.test(String(cssColor || '')) ? String(cssColor) : fallback;
    }

    return rgbToHex(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function attachPageEvents(pageShell) {
    pageShell.addEventListener('pointerdown', onPagePointerDown);
    pageShell.addEventListener('pointermove', onPagePointerMove);
    pageShell.addEventListener('pointerup', onPagePointerUp);
    pageShell.addEventListener('pointerleave', onPagePointerLeave);
    pageShell.addEventListener('touchstart', onPageTouchStart, { passive: false });
}

export async function runBackgroundPreparation(generation) {
    if (state.prepareQueueRunning) {
        return;
    }
    state.prepareQueueRunning = true;
    try {
        while (generation === state.renderGeneration) {
            const next = pickNextPageToPrepare();
            if (!next) {
                break;
            }
            await preparePage(next, generation);
            await idleBreath();
        }
    } finally {
        state.prepareQueueRunning = false;
    }
}

export function toPdfRect(annotation, pageLayout, pageSize) {
    const xScale = pageSize.width / pageLayout.screenWidth;
    const yScale = pageSize.height / pageLayout.screenHeight;

    return {
        x: annotation.x * xScale,
        y: pageSize.height - ((annotation.y + annotation.height) * yScale),
        width: annotation.width * xScale,
        height: annotation.height * yScale,
        xScale,
        yScale,
    };
}
